import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../../api/apiClient";
import { setToken } from "../../api/session";
import { isAdminToken } from "../../api/adminSession";
import { setWorkflowOwner } from "../../mock/localWorkflowStore";
import { AUTOFILL_SX } from "../../theme/formStyles";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PasswordVisibilityToggle from "../../components/PasswordVisibilityToggle";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Admin console sign-in.
 *
 * Credentials are verified against POST /auth/login, and admin rights come
 * from the `role` claim the backend puts in the returned JWT. A non-admin
 * account is signed straight back out here, and the API would refuse it
 * anyway: every /dashboard/* route re-checks the role server-side.
 */
function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    const errors = {};
    if (!email.trim()) {
      errors.email = "Email is required.";
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      errors.email = "Enter a valid email address.";
    }
    if (!password) {
      errors.password = "Password is required.";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const normalizedEmail = email.trim().toLowerCase();

    setLoading(true);
    try {
      const data = await loginUser(normalizedEmail, password);

      // Only the server can say who is an admin. Reject a standard account
      // here without ever storing its token, so signing in at the admin door
      // does not quietly log someone into the product.
      if (!isAdminToken(data.access_token)) {
        setError("This account does not have administrator access.");
        return;
      }

      setToken(data.access_token);
      setWorkflowOwner(normalizedEmail);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.detail || "Sign-in failed. Please check your credentials.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ px: 0 }}>
      <Paper elevation={3} sx={{ p: 4, width: "100%", borderRadius: 2 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 100%)",
              boxShadow: "0 8px 24px rgba(0,85,255,.35)",
            }}
          >
            <AdminPanelSettingsOutlinedIcon sx={{ color: "#fff", fontSize: 28 }} />
          </Box>
          <Typography variant="h5" align="center" fontWeight="bold">
            Admin Sign In
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            Sign in with your administrator account to manage users, quotes and
            blueprint sessions.
          </Typography>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="admin-email"
            name="email"
            label="Admin Email"
            placeholder="admin@onepassblueprint.com"
            autoComplete="email"
            autoFocus
            sx={AUTOFILL_SX}
            // Pinned open so an autofilled value can never sit under the label.
            InputLabelProps={{ shrink: true }}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFieldErrors((prev) => ({ ...prev, email: undefined }));
            }}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || " "}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="admin-password"
            name="password"
            label="Password"
            placeholder="Enter your password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            sx={AUTOFILL_SX}
            InputLabelProps={{ shrink: true }}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((prev) => ({ ...prev, password: undefined }));
            }}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password || " "}
            InputProps={{
              endAdornment: (
                <PasswordVisibilityToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((prev) => !prev)}
                />
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            startIcon={<LockOutlinedIcon />}
            sx={{ mt: 2, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In to Admin Console"}
          </Button>

          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Not an administrator?{" "}
              <MuiLink component={Link} to="/build/login" variant="body2">
                User sign in
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Typography
        variant="caption"
        align="center"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
        Admin accounts are provisioned internally. There is no self sign-up.
      </Typography>
    </Container>
  );
}

export default AdminLoginPage;
